import { computed } from "vue";
import store from "@/store";

/**
 * Composable para verificação de roles de usuários
 *
 * Uso:
 * import useRoleCheck from '@/composables/useRoleCheck'
 *
 * const { podeVerDadosGlobais, hasRole, hasAnyRole } = useRoleCheck()
 */
export default function useRoleCheck() {
  // Pega o usuário do Vuex store
  const user = computed(() => store.getters["Auth/user"]);

  // Roles que podem ver dados globais do município
  // Apenas: admin, super-admin, supervisor e coordenador-semed
  const ROLES_DADOS_GLOBAIS = ["admin", "super-admin", "supervisor", "coordenador-semed"];

  /**
   * Verifica se o usuário tem alguma das roles especificadas
   * @param {string[]} roleNames - Array de nomes de roles
   * @returns {boolean}
   */
  const hasAnyRole = (roleNames) => {
    if (!user.value || !user.value.roles) return false;
    return user.value.roles.some((role) => roleNames.includes(role.name));
  };

  /**
   * Verifica se o usuário tem uma role específica
   * @param {string} roleName - Nome da role
   * @returns {boolean}
   */
  const hasRole = (roleName) => {
    if (!user.value || !user.value.roles) return false;
    return user.value.roles.some((role) => role.name === roleName);
  };

  // Computed: pode ver dados globais do município?
  const podeVerDadosGlobais = computed(() => {
    return hasAnyRole(ROLES_DADOS_GLOBAIS);
  });

  // Computed: é professor?
  const isProfessor = computed(() => {
    return hasAnyRole(["professor", "prof-auxiliar"]);
  });

  // Computed: é profissional de apoio?
  const isProfissionalApoio = computed(() => {
    return hasAnyRole([
      "nutricionista",
      "psicologo(a)",
      "assistente-social",
      "auxiliar-servicos-gerais",
      "merendeira",
      "zelador(a)",
      "vigilante",
      "digitador",
      "monitor(a)",
      "psicopedagogo(a)",
      "diretor(a)-adjunto(a)",
    ]);
  });

  // Computed: é admin ou super-admin?
  const isAdmin = computed(() => {
    return hasAnyRole(["admin", "super-admin"]);
  });

  // Computed: é super-admin?
  const isSuperAdmin = computed(() => {
    return hasRole("super-admin");
  });

  return {
    podeVerDadosGlobais,
    isProfessor,
    isProfissionalApoio,
    isAdmin,
    isSuperAdmin,
    hasRole,
    hasAnyRole,
  };
}
